import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Zap, ChevronDown, ChevronUp, HelpCircle, Search } from 'lucide-react';
import { useState } from 'react';
import Footer from '../components/Footer';
import { useCanonical } from '../hooks/useCanonical';

const faqData = [
  {
    category: "General ECU Tuning",
    questions: [
      {
        q: "What is ECU tuning and how does it work?",
        a: "Your engine has a computer (the ECU) that controls how much fuel gets injected, when the spark fires, how much boost the turbo makes, and dozens of other things. The manufacturer programs it conservatively to work in every climate, on every fuel quality, in every country they sell to. When we tune it, we adjust those conservative settings for your specific situation - better fuel, reasonable climate, and your goals. We read the file off your ECU, modify the calibration maps, and send it back for you to flash."
      },
      {
        q: "Is ECU tuning safe for my engine?",
        a: "Short answer: yes, when done by people who know what they're doing. We keep our calibrations within the mechanical safety margins of your engine. A Stage 1 tune on a healthy engine is very low risk - we're optimising what's already there, not pushing beyond what the hardware can handle. Stage 2 and above requires supporting mods, and that's where you need to make sure everything is done properly. We're not going to sell you a tune that'll blow your engine up. That's bad for both of us."
      },
      {
        q: "Will ECU tuning void my warranty?",
        a: "It might, and we won't pretend otherwise. Most dealers can detect that a remap has been done. That said, they legally have to prove the modification caused whatever failure they're denying coverage for - they can't just blanket-reject everything. Plenty of our customers tune after the warranty expires. Others decide the performance improvement is worth the trade-off. It's your call - just go in with your eyes open."
      },
      {
        q: "How much power can I gain from ECU tuning?",
        a: "Depends on your engine. Turbocharged diesels see the biggest gains - typically 20-35% more power and torque from a Stage 1 tune. A 150 HP diesel will usually make 190-210 HP. Turbo petrols gain 15-25%. Naturally aspirated engines, honestly, don't expect miracles - 5-10% at best, though the throttle response improvement is noticeable. We'll give you realistic estimates for your specific vehicle when you order."
      },
      {
        q: "What's the difference between Stage 1, Stage 2, and Stage 3 tuning?",
        a: "Stage 1 is software only - no physical changes to your car. That's what most people go for and where the best value is. Stage 2 means you've fitted supporting hardware (exhaust, intercooler, intake) and the tune takes advantage of that extra breathing room. Stage 3 is serious build territory - bigger turbo, uprated fuel system, possibly forged internals. Each stage costs more and gives more power, but Stage 1 is the sweet spot for most people."
      },
      {
        q: "Can ECU tuning improve fuel economy?",
        a: "Yes, and it's one of the most underrated benefits. Our economy tunes optimise the fuel and boost maps for efficiency rather than peak power. We consistently see 8-15% fuel savings on diesels. The catch is that if you've got a performance tune and you're using all that extra power all the time, you'll use more fuel. Drive sensibly on a performance tune and economy stays about the same or slightly better than stock."
      },
      {
        q: "How long does the ECU tuning process take?",
        a: "From when your file lands in our inbox to when we send it back: 20-60 minutes for most jobs. Unusual ECUs or complex multi-system modifications might take longer, but we'll tell you upfront. The flashing process on your end takes another 5-30 minutes depending on your tool."
      },
      {
        q: "Can I revert to the original ECU file?",
        a: "Yes, always. That's why we tell everyone to keep their original file backed up. Flash it back in and your car is stock again. Useful if you're going to the dealer, selling the car, or just decide tuning isn't for you. It's completely reversible."
      },
      {
        q: "What vehicles do you support?",
        a: "Pretty much anything with an ECU. Cars, SUVs, trucks, vans, motorcycles, agricultural equipment, marine engines, heavy machinery. We specialise in European vehicles (BMW, Mercedes, Audi, VW, Volvo) but we work on Toyota, Ford, Nissan, Hyundai, Kia, and everything else regularly. If you're not sure about your specific vehicle, just ask."
      },
      {
        q: "Do I need any special equipment for ECU tuning?",
        a: "You'll need a tuning tool to read and write your ECU file. Common options are KESS V2, KTAG, CMD Flash, Autotuner, and a few others. Some cars can be done through the OBD port under the dash (quickest method). Others need the ECU removed for bench or boot mode access. If you're not sure which tool or method suits your vehicle, we'll point you in the right direction."
      }
    ]
  },
  {
    category: "DPF Delete",
    questions: [
      {
        q: "What is a DPF and why would I want to delete it?",
        a: "A DPF (Diesel Particulate Filter) traps soot from your exhaust. In theory, it burns the soot off during 'regeneration' when exhaust temps get high enough. In practice, if you don't do enough highway driving, the filter clogs up. And replacement costs $2,000-8,000 depending on the vehicle. DPF delete removes the filter and reprograms the ECU so it stops trying to regenerate and doesn't throw any error codes."
      },
      {
        q: "Is DPF delete legal?",
        a: "For road vehicles, no - in most countries, removing emissions equipment from a road-registered vehicle is illegal. Our DPF delete service is provided for off-road, agricultural, competition, and export vehicles only. What you do with your own property is your business, but we do recommend checking your local regulations. We're not in a position to give legal advice."
      },
      {
        q: "Will my vehicle pass emissions testing after DPF delete?",
        a: "No. If your local inspection includes particulate measurement or an OBD check for DPF status, it won't pass. If you need your vehicle to pass emissions testing, DPF delete isn't for you. It's that simple."
      },
      {
        q: "What are the benefits of DPF delete?",
        a: "No more clogged filters. No more $3,000 replacement bills. No more regen cycles burning extra fuel. Fuel economy improves (typically 5-10%) because the engine isn't dumping fuel trying to heat up the filter. Throttle response sharpens because there's less backpressure. And you stop worrying every time the DPF light comes on. For work vehicles that never see a highway, it's a no-brainer."
      },
      {
        q: "Does DPF delete require physical removal of the filter?",
        a: "For the best result, yes. The filter should be gutted or replaced with a straight pipe. Leaving a clogged DPF in place means you still have backpressure issues even with the software side sorted. We handle the ECU modification - a local exhaust shop or mechanic can handle the physical removal. Usually $100-300 for the pipe work."
      },
      {
        q: "What symptoms indicate a failing DPF?",
        a: "The regen light comes on more and more often. The engine feels sluggish and fuel economy gets worse (extra fuel is being dumped trying to burn off soot). Eventually the DPF warning stays on permanently and the car goes into limp mode. At that point, the dealer usually quotes you for a new filter. We've seen people spend $5,000+ trying to keep a DPF alive before finally calling us."
      },
      {
        q: "Can a clogged DPF damage my engine?",
        a: "Yes. A severely blocked filter creates so much backpressure that it stresses the turbocharger. Failed regen cycles inject extra fuel that washes past the piston rings into the oil, diluting it. That diluted oil then doesn't protect the bearings properly. We've seen engines go from 'DPF light on' to 'major mechanical failure' because the problem was ignored for too long."
      },
      {
        q: "How much does DPF delete cost?",
        a: "Our DPF delete software modification is $80. That covers disabling all regen strategies, removing every DPF-related fault code, and checksum correction. The physical removal and pipe fabrication is done by your local workshop - typically $100-300."
      }
    ]
  },
  {
    category: "EGR Delete",
    questions: [
      {
        q: "What is EGR and what problems does it cause?",
        a: "EGR stands for Exhaust Gas Recirculation. It feeds hot, sooty exhaust back into your intake manifold to lower combustion temperatures and reduce NOx emissions. The problem is that the soot and oil vapour mix together and create thick, tar-like carbon deposits that gradually clog the intake. We've pulled intake manifolds off 90,000 km engines that were 60% blocked. It also causes EGR valve failures and EGR cooler cracks (the Ford 6.0 Power Stroke is notorious for this)."
      },
      {
        q: "What are the benefits of EGR delete?",
        a: "The engine breathes clean air instead of recirculated exhaust. You'll notice the difference in throttle response almost immediately - most customers say the engine feels 'crisper.' Fuel economy improves slightly (3-8%) because combustion is cleaner. No more carbon buildup in the intake. No more $300-800 EGR valve replacements. And the intake temperatures drop, which is better for everything."
      },
      {
        q: "Can I delete EGR without deleting DPF?",
        a: "Yes, they're completely separate systems. Lots of customers do EGR only. One thing to be aware of: without EGR, the engine produces marginally more soot, which can speed up DPF loading slightly. If your DPF is already struggling, doing both at the same time makes sense. But EGR-only works perfectly fine on its own."
      },
      {
        q: "Will EGR delete cause check engine lights?",
        a: "Not if the software side is done properly. We disable all EGR-related monitoring in the ECU - valve position, flow rate, temperature - and remove every associated fault code. Without the ECU modification, disconnecting or blanking the EGR will absolutely trigger codes and potentially limp mode. The software is essential."
      },
      {
        q: "Should I physically remove or blank the EGR?",
        a: "At minimum, fit a blanking plate to stop exhaust gases getting into the intake. That's a $10-30 steel plate that blocks the passage. Full removal (valve and cooler) is a cleaner job but more involved. Either way works - the blanking plate is quicker and cheaper, and it's what most people go with."
      },
      {
        q: "How much does EGR delete cost?",
        a: "Our EGR delete file modification is $60. Includes full EGR deactivation, all related DTC removal, and checksum correction. We usually recommend combining it with DPF delete ($80) if you're already touching the emissions systems - we can do everything in one file. Blanking plates are $10-30 on top."
      }
    ]
  },
  {
    category: "AdBlue/SCR Delete",
    questions: [
      {
        q: "What is AdBlue and why is it used?",
        a: "AdBlue (called DEF in North America) is a urea solution that gets sprayed into the exhaust to break down nitrogen oxides. It's been mandatory on most diesels since about 2015. The system works, but it's complicated - you've got a heated tank, a pressurised pump, a dosing injector, 2-3 NOx sensors, and an SCR catalyst. That's a lot of things that can break, and they do."
      },
      {
        q: "What problems can the AdBlue system cause?",
        a: "Where do we start? Crystallised injectors ($500-1,500 to replace). Failed AdBlue pumps ($1,500-3,500). Dead NOx sensors ($400-1,200 each, and there are usually two or three). Tank heater failures in cold weather. And the worst part: most vehicles progressively limit your speed when the system faults, and some will refuse to restart entirely. We've had customers towed to workshops because their BMW or Mercedes wouldn't start at a service station."
      },
      {
        q: "What does AdBlue delete include?",
        a: "We disable the entire SCR system in the ECU: AdBlue injection, all NOx sensor monitoring, SCR efficiency calculations, tank level and quality warnings, every speed limiter tied to the AdBlue system, and all related fault codes. After our modification, the vehicle runs completely normally with no warnings and no need for AdBlue fluid."
      },
      {
        q: "Will I still need to fill AdBlue after the delete?",
        a: "No. The system is completely disabled. You'll never buy another bottle of AdBlue. Most people leave the tank in place (empty). Some disconnect it entirely. Either way, the ECU doesn't care anymore."
      },
      {
        q: "Is AdBlue delete suitable for trucks and commercial vehicles?",
        a: "Absolutely - commercial vehicles are actually where it makes the most business sense. The AdBlue consumption on a truck doing high mileage adds up fast, and a single pump failure can put a vehicle off the road for days while parts are sourced. We do DAF, MAN, Scania, Volvo, Mercedes Actros, Iveco, Renault - all the major commercial platforms."
      },
      {
        q: "What is the NOx sensor and why does it fail?",
        a: "NOx sensors sit in the exhaust and measure nitrogen oxide levels before and after the SCR catalyst. The ECU compares the two readings to check if the AdBlue system is doing its job. The problem is these sensors live in an extremely harsh environment - extreme heat, corrosive gases - and they degrade over time. When they start giving inaccurate readings, the ECU triggers the AdBlue warning countdown. At $400-1,200 per sensor, it adds up fast."
      },
      {
        q: "How much does AdBlue delete cost?",
        a: "Our AdBlue delete is $100 for passenger vehicles and $150-250 for trucks and commercial vehicles. That includes disabling the entire SCR system, all NOx sensor monitoring, speed limiter removal, and every related fault code. If you're doing DPF and EGR as well, we combine everything into one file."
      }
    ]
  },
  {
    category: "DTC Removal",
    questions: [
      {
        q: "What is DTC removal?",
        a: "When your ECU detects something outside its expected parameters, it stores a fault code (DTC) and turns on the check engine light. DTC removal means we go into the ECU file and disable the specific monitoring routine that generates that particular code. The light goes off permanently. We're not clearing codes with a scanner (they'd come back) - we're disabling the trigger at the software level."
      },
      {
        q: "What DTCs can you remove?",
        a: "Basically any of them. EGR codes (P0400-P0409), DPF codes (P2002-P2004), AdBlue/SCR codes (P20EE, P203F), catalyst codes (P0420-P0430), oxygen sensor codes, swirl flap codes (the infamous P2015 on VW), fuel system codes - you name it. Give us the code numbers or describe what light is on and we'll sort it."
      },
      {
        q: "Is DTC removal permanent?",
        a: "Yes. Once we disable the monitoring routine in the ECU software, that code doesn't come back. The only way it returns is if you flash a different ECU file that has the monitoring re-enabled. You can always revert to your original file if you want everything back to standard."
      },
      {
        q: "Will DTC removal fix the underlying problem?",
        a: "No, and we want to be upfront about that. We're disabling the warning, not fixing the component. If your catalytic converter is genuinely failing, removing the P0420 code won't make the cat work better. DTC removal is for situations where you've intentionally modified something (like a DPF delete), a sensor has been removed, or the repair cost doesn't make sense on an older vehicle. Don't use it to hide problems."
      },
      {
        q: "How much does DTC removal cost?",
        a: "Straightforward pricing: $10 for one code, $20 for 2-6 codes, $30 for 7 or more. Plus $5 for checksum correction if needed. If a related code pops up after our modification, we'll remove it for free."
      },
      {
        q: "What information do I need to provide for DTC removal?",
        a: "The fault code numbers (P0420, P2002, etc.) are ideal. If you don't have a scanner, just describe the warning lights and symptoms and we'll work from that. We also need your ECU file and basic vehicle details. The more specific you can be, the quicker we can turn it around."
      }
    ]
  },
  {
    category: "Technical & Process",
    questions: [
      {
        q: "What tools do I need to read/write my ECU?",
        a: "You need an ECU reading/writing tool that supports your vehicle. The popular ones are KESS V2, KTAG, CMD Flash, Autotuner, BitBox, and New Genius. Each tool supports different vehicles and access methods. Some cars can be done through the OBD port (easiest), others need the ECU pulled for bench or boot mode access. Tell us your vehicle and we'll recommend the right approach."
      },
      {
        q: "How do I read my ECU file?",
        a: "Depends on your vehicle and tool. OBD reading is the simplest - plug into the diagnostic port under the dash, follow the tool's prompts, and it pulls the file. Bench reading means the ECU comes out of the vehicle and you connect to it on a workbench. Boot mode is the most involved - you open the ECU housing and connect to specific pins on the circuit board. Most European cars can be done via OBD. Toyota/Lexus almost always need bench mode."
      },
      {
        q: "What file formats do you support?",
        a: "All of them. .bin, .ori, .mod, .frf, .sgm, encrypted formats from various tuning tools - just send us whatever your tool gives you. We'll identify the format and ECU type on our end. Don't worry about converting or renaming anything."
      },
      {
        q: "What is checksum correction and do I need it?",
        a: "The ECU file contains checksum values - basically a mathematical fingerprint that proves the data hasn't been tampered with. When we modify the maps, the checksum no longer matches. Some ECUs will reject the file outright if the checksum is wrong. Others don't check. We correct the checksum on every file that needs it - it's included at $5 and we do it automatically."
      },
      {
        q: "Do you offer a warranty on your work?",
        a: "Yes - 30 days from delivery. If you have any issues directly caused by our file modification, we'll revise it at no cost. That includes missed fault codes, calibration adjustments, or anything else that's our responsibility. If your check engine light comes on because of our tune, we fix it. Free."
      },
      {
        q: "How do I flash the modified file to my ECU?",
        a: "Same tool, same method, reverse direction. Where you chose 'Read' before, you now choose 'Write' or 'Flash' and select the modified file we sent you. Takes 5-30 minutes. Two important things: keep a battery charger connected the entire time, and don't touch anything until the tool says it's complete."
      },
      {
        q: "What happens if something goes wrong during flashing?",
        a: "Don't panic. Most of the time, you can just retry the write. Professional tuning tools have safety features for exactly this situation. In the rare case the ECU gets corrupted (usually from a power interruption mid-flash), it can almost always be recovered through bench or boot mode. We provide support for these situations and can guide you through recovery."
      },
      {
        q: "Can I tune my vehicle multiple times?",
        a: "As many times as you want. ECU flash memory doesn't wear out from normal use. Loads of our customers start with Stage 1, enjoy it for a while, then come back for Stage 2 after fitting hardware. Each tune is a separate file modification, but we look after returning customers."
      }
    ]
  }
];

const FAQPage = () => {
  useCanonical('https://ecuflashservice.com/faq');
  const [openItems, setOpenItems] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const filteredFAQ = faqData.map(category => ({
    ...category,
    questions: category.questions.filter(
      q => q.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
           q.a.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  const totalQuestions = faqData.reduce((acc, cat) => acc + cat.questions.length, 0);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.flatMap(category => 
      category.questions.map(q => ({
        "@type": "Question",
        "name": q.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": q.a
        }
      }))
    )
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>ECU Tuning FAQ | DPF EGR AdBlue Questions Answered | 45+ Expert Answers</title>
        <meta name="description" content="Get answers to 45+ ECU tuning questions. Is DPF delete legal? How long does EGR removal take? Will AdBlue delete cause problems? Expert answers from professional tuners." />
        <meta name="keywords" content="ECU tuning FAQ, DPF delete questions, EGR delete FAQ, AdBlue removal questions, is chip tuning safe, ECU remap questions" />
        <meta property="og:title" content={`ECU Tuning FAQ - ${totalQuestions}+ Questions Answered`} />
        <meta property="og:description" content="Comprehensive FAQ covering ECU tuning, emissions system deletes, and engine remapping." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://ecuflashservice.com/favicon-512.png" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-[#1A1A1A] border border-white/10 flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <span className="font-heading text-lg font-bold uppercase tracking-wide text-white">ECU Flash Service</span>
            </Link>
            <Link to="/" className="btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-4 py-2 text-sm hover:bg-white transition">
              <span>Get Started</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-6 bg-gradient-to-b from-[#0A0A0A] to-[#050505]">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-[#00F0FF]/30 mb-6">
            <HelpCircle className="w-4 h-4 text-[#00F0FF]" />
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00F0FF]">Knowledge Base</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase mb-4">
            <span className="text-white">Frequently Asked</span>{' '}
            <span className="text-[#00F0FF]">Questions</span>
          </h1>
          <p className="text-[#888] text-lg mb-8">
            {totalQuestions}+ questions answered by our tuning team
          </p>

          {/* Search */}
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666]" />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#1A1A1A] border border-white/10 text-white pl-12 pr-4 py-3 focus:border-[#00F0FF]/50 focus:outline-none"
            />
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          {filteredFAQ.map((category, categoryIndex) => (
            <div key={categoryIndex} className="mb-12">
              <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-[#00F0FF] mb-6 pb-2 border-b border-white/10">
                {category.category}
              </h2>
              <div className="space-y-3">
                {category.questions.map((item, questionIndex) => {
                  const key = `${categoryIndex}-${questionIndex}`;
                  const isOpen = openItems[key];
                  return (
                    <div key={questionIndex} className="bg-[#0A0A0A] border border-white/10 hover:border-white/20 transition">
                      <button
                        onClick={() => toggleItem(categoryIndex, questionIndex)}
                        className="w-full flex items-center justify-between p-5 text-left"
                      >
                        <span className="text-white font-semibold pr-4">{item.q}</span>
                        {isOpen ? (
                          <ChevronUp className="w-5 h-5 text-[#00F0FF] flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-[#666] flex-shrink-0" />
                        )}
                      </button>
                      {isOpen && (
                        <div className="px-5 pb-5 pt-0">
                          <p className="text-[#888] leading-relaxed">{item.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {filteredFAQ.length === 0 && (
            <div className="text-center py-12">
              <p className="text-[#888]">No questions found matching "{searchTerm}"</p>
            </div>
          )}
        </div>
      </section>

      {/* Ad Placement */}
      <section className="py-8 px-6">
        <div className="container mx-auto max-w-4xl">
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-4">Still Have Questions?</h2>
          <p className="text-[#888] mb-6">Our team is here to help with anything specific to your vehicle or situation.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-6 py-3 hover:bg-white transition">
              <span>Contact Us</span>
            </Link>
            <Link to="/" className="btn-skew border border-white/30 text-white font-bold uppercase tracking-wider px-6 py-3 hover:border-[#00F0FF] transition">
              <span>Start Order</span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQPage;
